
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export interface ChangelogEntry {
  version: string;
  date: string;
  changes: string[];
}

export function useChangelog() {
  const [currentVersion, setCurrentVersion] = useState("2.2.0-wip"); // Updated to reflect current WIP version
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [changelog, setChangelog] = useState<ChangelogEntry[]>([
    {
      version: "2.2.0-wip", // New WIP version
      date: "2025-06-11", // Current date
      changes: [
        "Enhanced onboarding flow: Improved authentication redirect logic and better handling of edge cases",
        "Conservative cleanup system: Smart detection and resolution of stuck onboarding states",
        "Improved organization setup: Better error handling for organization member management",
        "Robust redirect logic: Enhanced navigation flow from authentication to dashboard/onboarding",
        "Better error recovery: More graceful handling of authentication and setup failures",
        "Internal versioning system: Workaround for read-only package.json with fallback versioning",
        "Enhanced settings management: Version synchronization and improved configuration handling",
        "Improved user experience: Reduced friction in onboarding and setup processes",
        "Database optimizations: Better handling of profile and organization data relationships",
        "Security enhancements: More secure authentication state management and validation"
      ]
    },
    {
      version: "2.1.0-WIP", // Previous WIP version moved down
      date: "2025-06-10",
      changes: [
        "Microsoft 365 OAuth integration: Secure authentication and token management",
        "Email sync agent: Real-time email processing and synchronization",
        "Calendar integration: Free/busy scheduling and event management",
        "Connection health monitoring: Automatic token refresh and status tracking",
        "Enhanced Microsoft 365 settings: Service management and configuration",
        "Route fixes: Added missing routes for Inbox, Client BizDev, and Integrations pages",
        "Improved error handling: Better integration failure management",
        "Security enhancements: Encrypted credential storage and secure API calls",
        "Performance optimizations: Faster email processing and reduced latency",
        "Database migrations: Improved data integrity and unique constraints",
        "Enhanced onboarding flow: Better error handling and user experience"
      ]
    },
    {
      version: "2.0.0", // Major stable release
      date: "2025-05-15",
      changes: [
        "Complete application restructure: Modern component architecture and routing",
        "Enhanced AI agent orchestration: Advanced A2A protocol implementation",
        "Unified inbox system: Comprehensive email processing and management",
        "Advanced candidate matching: AI-powered pipeline management and screening",
        "Client business development tools: Revenue tracking and relationship management",
        "Comprehensive analytics dashboard: Predictive insights and performance metrics",
        "Job search automation: Multiple provider integrations (LinkedIn, Indeed, ZipRecruiter)",
        "Calendar management: Event orchestration and scheduling automation",
        "Settings management: API key validation and configuration",
        "Beta program dashboard: User metrics and system monitoring",
        "Landing page redesign: Agent hierarchy showcase and modern branding",
        "Enhanced error handling: Global error boundaries and loading states",
        "Improved routing system: Proper navigation and authentication flow",
        "Database optimizations: Performance improvements and security enhancements",
        "Versioning system: Component-level version control and rollback capabilities"
      ]
    },
    {
      version: "1.3.0",
      date: "2025-05-13",
      changes: [
        "Updated sidebar logo to red with black light rays.",
        "Adjusted Recruitment Analytics section layout to utilize full page width and prevent card overlap on Analytics page.",
      ],
    },
    {
      version: "1.2.0",
      date: "2025-04-24",
      changes: [
        "Enhanced Interview Scheduling: Unified all scheduling capabilities under single agent",
        "Improved A2A Protocol: Expanded messaging API with full message validation",
        "Added Context Management: Implemented reliable context storage with TTL",
        "Security: Added row-level security to context storage",
        "Performance: Added database indices for context querying",
        "Maintenance: Implemented automated cleanup for expired context data",
        "Protocol: Added support for MCP (Multi-Context Protocol) message types",
        "API: Added new edge functions for context operations",
        "Developer Experience: Introduced useModelContext hook for easier context handling"
      ]
    },
    {
      version: "1.0.0",
      date: "2025-04-20",
      changes: [
        "Initial release",
        "Core recruiting functionality",
        "AI-powered candidate matching",
        "Calendar integration"
      ]
    }
  ]);

  useEffect(() => {
    const channel = supabase.channel('changelog')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'app_versions'
        },
        (payload) => {
          const newVersion = payload.new as ChangelogEntry;
          // Check if this version is already in the hardcoded list to avoid duplicates
          // if it's coming from a real-time update.
          if (!changelog.some(entry => entry.version === newVersion.version)) {
            setChangelog(prev => [newVersion, ...prev]);
          }
          // Only set hasNewVersion if the incoming version is newer than current
          if (newVersion.version > currentVersion) {
            setHasNewVersion(true);
            toast.info("New Version Available", {
              description: `Version ${newVersion.version} has been released with new features and improvements.`
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [changelog, currentVersion]); // Added dependencies to useEffect

  return {
    currentVersion,
    hasNewVersion,
    changelog
  };
}
